import { useEffect, useState } from "react";
import { ArrowLeft, FileText, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { fieldKind, FIELD_HINTS, fillTemplate } from "./templateUtils";

const FillForm = ({ tpl, onBack, onApply }) => {
    const [values, setValues] = useState({});
    const set = (f) => (e) => setValues({ ...values, [f]: e.target.value });
    return (
        <div className="space-y-4" data-testid="mailing-template-fill">
            <button onClick={onBack} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#0D4F3C]"><ArrowLeft size={12} /> Wróć do listy</button>
            <h4 className="font-serif text-xl text-[#092E22]">{tpl.name}</h4>
            {tpl.fields.length === 0 && <p className="text-sm text-gray-500">Ten szablon nie ma pól do uzupełnienia.</p>}
            {tpl.fields.map((f) => (
                <div key={f} className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-gray-600">{f}</Label>
                    {fieldKind(f) === "textarea" ? (
                        <Textarea rows={3} value={values[f] || ""} onChange={set(f)} data-testid={`mailing-template-field-${f}`} />
                    ) : (
                        <Input type={fieldKind(f) === "date" ? "date" : "text"} list={f === "KLATKA" ? "klatki-list" : undefined}
                            value={values[f] || ""} onChange={set(f)} placeholder={FIELD_HINTS[f] || ""} data-testid={`mailing-template-field-${f}`} />
                    )}
                </div>
            ))}
            <datalist id="klatki-list">{["25C", "25D", "25E", "25F", "25 C/D/E/F"].map((k) => <option key={k} value={k} />)}</datalist>
            <p className="text-[11px] text-gray-400">Puste pola zostaną w treści jako [POLE] – możesz je uzupełnić później w edytorze.</p>
            <Button onClick={() => onApply(fillTemplate(tpl, values), values.DATA || "")} className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white" data-testid="mailing-template-apply">
                Użyj szablonu
            </Button>
        </div>
    );
};

export const TemplatePickerModal = ({ onClose, onApply }) => {
    const [templates, setTemplates] = useState([]);
    const [selected, setSelected] = useState(null);
    useEffect(() => {
        api.get("/mailing/templates").then((r) => setTemplates(r.data || [])).catch(() => toast.error("Nie udało się wczytać szablonów."));
    }, []);

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()} data-testid="mailing-template-picker">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="font-serif text-2xl text-[#092E22]">Wybierz szablon</h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="mailing-template-picker-close"><X size={18} /></button>
                </div>
                {selected ? (
                    <FillForm tpl={selected} onBack={() => setSelected(null)} onApply={onApply} />
                ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                        {templates.length === 0 && <p className="text-sm text-gray-400">Brak szablonów.</p>}
                        {templates.map((t) => (
                            <button key={t.id} onClick={() => setSelected(t)} data-testid={`mailing-template-option-${t.id}`}
                                className="text-left p-4 rounded-2xl border border-gray-100 bg-[#FAF8F3] hover:border-[#0D4F3C]/40 hover:-translate-y-0.5 transition-[transform,border-color]">
                                <p className="text-[10px] uppercase tracking-widest text-[#B88A26] mb-1">{t.category}</p>
                                <p className="font-semibold text-sm text-[#092E22] flex items-center gap-2"><FileText size={14} />{t.name}</p>
                                <p className="text-xs text-gray-500 mt-1 line-clamp-1">{t.subject}</p>
                                {t.fields.length > 0 && <p className="text-[10px] text-gray-400 mt-2 font-mono line-clamp-1">{t.fields.map((f) => `[${f}]`).join(" ")}</p>}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TemplatePickerModal;
